// ===== 维度定义 =====
export type DimensionId = 'D1' | 'D2' | 'D3' | 'D4';

// 各维度的极点字母
export const DIMENSION_POLES = {
  D1: { A: 'A', B: 'R' }, // 锚定来源: Anchor / Reference
  D2: { A: 'S', B: 'C' }, // 处理模式: Systematic / Contextual
  D3: { A: 'I', B: 'E' }, // 驱动来源: Intrinsic / Extrinsic
  D4: { A: 'D', B: 'O' }, // 不确定性态度: Definite / Open
} as const;

// 维度极点值类型
export type D1Pole = 'internal' | 'external';
export type D2Pole = 'systematic' | 'contextual';
export type D3Pole = 'intrinsic' | 'extrinsic';
export type D4Pole = 'closure' | 'exploration';

// ===== 情境定义 =====
export type ContextType = 'natural' | 'work' | 'stress' | 'mixed';

// ===== 16类型 =====
export type TypeCode = 
  | 'ASID' | 'ASIO' | 'ASED' | 'ASEO'
  | 'ACID' | 'ACIO' | 'ACED' | 'ACEO'
  | 'RSID' | 'RSIO' | 'RSED' | 'RSEO'
  | 'RCID' | 'RCIO' | 'RCED' | 'RCEO';

export const TYPE_NAMES: Record<TypeCode, { zh: string; en: string }> = {
  ASID: { zh: '逻辑-自治型', en: 'Logic-Autonomy' },
  ASIO: { zh: '架构师-探险家型', en: 'Architect-Explorer' },
  ASED: { zh: '战略-统筹型', en: 'Strategy-Coordinator' },
  ASEO: { zh: '博弈-开拓型', en: 'Strategist-Pioneer' },
  ACID: { zh: '匠人-沉潜型', en: 'Craftsman-Deep' },
  ACIO: { zh: '灵性-体验型', en: 'Spiritual-Experiential' },
  ACED: { zh: '策展-守护型', en: 'Curator-Guardian' },
  ACEO: { zh: '体验-创造型', en: 'Experience-Creator' },
  RSID: { zh: '准则-效忠型', en: 'Principle-Loyalist' },
  RSIO: { zh: '解构-评论型', en: 'Analyst-Critic' },
  RSED: { zh: '功利-架构型', en: 'Pragmatic-Architect' },
  RSEO: { zh: '冒险-竞争型', en: 'Adventurer-Competitor' },
  RCID: { zh: '共情-调和型', en: 'Empathy-Harmonizer' },
  RCIO: { zh: '情感-雷达型', en: 'Emotional-Radar' },
  RCED: { zh: '务实-传统型', en: 'Pragmatic-Traditional' },
  RCEO: { zh: '表演-情境型', en: 'Performer-Adaptive' },
};

// ===== 题目类型 =====
export type QuestionType = 'single' | 'cross_validation';

export interface SingleQuestion {
  id: string;
  type: 'single';
  dimension: DimensionId;
  context: ContextType;
  indicator: string;
  scenario: string;
  optionA: {
    choices: string[];
  };
  optionB: {
    choices: string[];
  };
  // A选项指向极点A还是极点B
  optionADirection: 'A' | 'B';
}

// 维度极点字母类型
export type DimensionPoleLetter = 'A' | 'R' | 'S' | 'C' | 'I' | 'E' | 'D' | 'O';

export interface CrossValidationQuestion {
  id: string;
  type: 'cross_validation';
  dimension: DimensionId[];
  context: ContextType;
  indicator: string;
  scenario: string;
  options: {
    id: string;
    choices: string[];
    mapping: Partial<Record<DimensionId, DimensionPoleLetter>>;
  }[];
}

export type Question = SingleQuestion | CrossValidationQuestion;

// ===== 作答 =====
export interface Answer {
  questionId: string;
  selected: 'A' | 'B' | string; // A/B 或选项ID
  timestamp: number;
}

// ===== 计分结果 =====
export interface ContextScore {
  poleACount: number;
  poleBCount: number;
  result: string; // 极点ID: 'internal', 'external' 等
  resultLetter: string; // 'A', 'R', 'S', 'C', 'I', 'E', 'D', 'O'
  ratio: string; // '7:2'
  isWeak: boolean;
}

export interface DimensionResult {
  dimensionId: DimensionId;
  natural: ContextScore;
  work: ContextScore;
  stress: ContextScore;
  stability: StabilityType;
}

// ===== 稳定性 =====
export type StabilityType = 
  | 'stable'           // 完全稳定：三层相同
  | 'work_adaptive'    // 工作适应：自然≠工作=应激
  | 'stress_switch'    // 应激切换：自然=工作≠应激
  | 'gradual'          // 渐进变化：同方向递进
  | 'flexible';        // 高弹性：其他

export type OverallStabilityType =
  | 'core_stable'      // 核心稳定型（4个稳定轴）
  | 'single_flex'      // 单点弹性型（3个稳定轴）
  | 'context_adaptive' // 情境适应型（2个稳定轴）
  | 'highly_flexible'; // 高度弹性型（0-1个稳定轴）

// ===== 三层模式结果 =====
export interface ModeResult {
  natural: TypeCode;
  work: TypeCode;
  stress: TypeCode;
}

// ===== 交叉验证 =====
export type ReliabilityLevel = 'reliable' | 'attention_needed' | 'review_suggested';

export interface CrossValidationDimensionCheck {
  dimension: DimensionId;
  cvResult: DimensionPoleLetter;
  singleResult: string;
  consistent: boolean;
}

export interface CrossValidationDetail {
  questionId: string;
  selectedOption: string;
  dimensionChecks: CrossValidationDimensionCheck[];
  allConsistent: boolean;
}

export interface CrossValidationResult {
  rate: number;  // 一致率百分比 (0-100)
  reliable: ReliabilityLevel;
  details: CrossValidationDetail[];
  summary: {
    totalChecks: number;
    consistentChecks: number;
    inconsistentChecks: number;
  };
}

// ===== 完整测试结果 =====
export interface TestResult {
  responseId: string;
  version: string;
  timestamp: string;
  duration: number;
  participantTag?: string;
  answers: Answer[];
  dimensions: DimensionResult[];
  modes: ModeResult;
  overallStability: OverallStabilityType;
  stableAxes: DimensionId[];
  flexibleAxes: { dimension: DimensionId; pattern: StabilityType }[];
  crossValidation: CrossValidationResult;
}

// ===== 问卷配置 =====
export interface DimensionConfig {
  id: DimensionId;
  name: { zh: string; en: string };
  poleA: { id: string; letter: string; label: { zh: string; en: string } };
  poleB: { id: string; letter: string; label: { zh: string; en: string } };
}

export interface ContextConfig {
  id: ContextType;
  name: { zh: string; en: string };
  description: { zh: string; en: string };
}

export interface QuestionnaireConfig {
  version: string;
  dimensions: DimensionConfig[];
  contexts: ContextConfig[];
  questions: Question[];
  scoringRules: {
    threshold: number;
    weakThreshold: number;
  };
}

// ===== 多语言支持 =====
export type Locale = 'zh' | 'en';